import React, { useEffect, useState } from 'react'

import moment from 'moment-timezone'
import { useHistory } from 'react-router-dom'

import * as S from './styles'
import { useStores } from 'presentation/hooks/use-stores'
import { Profile } from 'common/enum/profile'
import { dateMask } from 'presentation/utils/masks'
import { dateInputFormat } from 'presentation/utils/date-format'
import { SearchSurgicalOrders } from 'domain/usecases/surgical-order/search-surgical-orders'
import getSlugByText from 'common/utils/getSlugByText'
import { CrmoFilter } from 'main/adapters/current-filters'
import { Sum } from 'presentation/utils/sum'
import { SurgicalPendencyStatus } from 'domain/entities/surgical-pendency'
import { ReactComponent as NewPendencyIcon } from 'presentation/assets/icons/new-pendency-icon.svg'
import { ReactComponent as CalendarCheckIcon } from 'presentation/assets/icons/calendar_check.svg'
import { ReactComponent as CalendarCancelIcon } from 'presentation/assets/icons/calendar_cancel.svg'
import { ReactComponent as AccordionArrow } from 'presentation/assets/icons/accordion-arrow.svg'
import { ReactComponent as CalendarRange } from 'presentation/assets/icons/calendar-range.svg'
import { ReactComponent as MedicalPrescritionIcon } from 'presentation/assets/icons/medical_prescrition.svg'
import { ReactComponent as ChatReversoIcon } from 'presentation/assets/icons/chat-reverso-icon.svg'
import { ReactComponent as CancelationRequestIcon } from 'presentation/assets/icons/cancelation-request-icon.svg'
import { TooltipNew } from 'presentation/shared/components/TooltipNew'
import { SurgicalOrderStatus } from 'common/enum/surgical-order-status'
import { GetSurgialOrderCounter } from 'domain/usecases/surgical-order/get-surgical-order-counter'
import HeadingNew from 'presentation/shared/components/HeadingNew'
import ContainerNew from 'presentation/hospital/components/ContainerNew'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import CardCounter from 'presentation/hospital/components/CardCounter'
import CardCounterMultiple from 'presentation/hospital/components/CardCounterMultiple'
import theme from 'presentation/styles/theme'
import { AuthorizationStatus } from 'common/enum/authorization-status'
import SelectFieldNew from 'presentation/shared/components/SelectFieldNew'

export type SurgicalOrderList = {
  surgery_request_id?: string
  status?: string
  unit?: string
  room?: string
  date?: string
  patient?: string
  birthDate?: string
  gender?: string
  insurance?: string
  doctor?: string
  surgery?: string
}

type Props = {
  counters?: GetSurgialOrderCounter.Model
  changeFilter?: (
    start: string,
    end: string,
    label?: string,
    params?: SearchSurgicalOrders.Params
  ) => void
  hospitalName?: string
  crmoDefaultFilters: CrmoFilter
}

export default function HospitalHomeLayout({
  changeFilter,
  hospitalName,
  counters,
  crmoDefaultFilters
}: Props) {
  const history = useHistory()
  const [showCalendar, setShowCalendar] = useState<'start' | 'end' | ''>()
  const [dates, setDates] =
    useState<{ start?: string; end?: string; label?: string }>(
      crmoDefaultFilters
    )

  const [isLoaded, setIsLoaded] = useState<boolean>(false)
  const user = useStores().currentAccount?.getCurrentAccount()?.user

  const toggleCalendar = (order: 'start' | 'end') => {
    if (showCalendar === order) {
      setShowCalendar('')
    } else {
      setShowCalendar(order)
    }
  }

  const changeDate = (date: Date) => {
    if (showCalendar === 'start' && !moment(date).isSameOrBefore(dates.end)) {
      return setShowCalendar('')
    } else if (
      showCalendar === 'end' &&
      moment(dates[showCalendar]) &&
      !moment(date).isSameOrAfter(dates.start)
    ) {
      return setShowCalendar('')
    }

    if (showCalendar) {
      const normalizedDate = moment(date).tz('America/Sao_Paulo').utc()

      const dateValue =
        showCalendar === 'start'
          ? normalizedDate.startOf('day').toISOString()
          : normalizedDate.endOf('day').toISOString()
      setDates({
        ...dates,
        [showCalendar]: dateValue,
        label: 'personalized'
      })
    }
    return setShowCalendar('')
  }

  const changeInterval = (interval: string) => {
    const today = moment().tz('America/Sao_Paulo').utc()
    const dates: {
      start?: string
      end?: string
      label?: string
    } = {
      end: today.endOf('day').toISOString(),
      label: interval
    }

    switch (interval) {
      case 'today':
        dates.start = today.startOf('day').toISOString()
        break
      case 'yesterday':
        dates.start = today.subtract('1', 'days').startOf('day').toISOString()
        dates.end = today.endOf('day').toISOString()
        break
      case 'week':
        dates.start = today.subtract('1', 'week').startOf('day').toISOString()
        break
      case 'month':
        dates.start = today.subtract('1', 'month').startOf('day').toISOString()
        break
      case 'trimester':
        dates.start = today.subtract('3', 'month').startOf('day').toISOString()
        break
      case 'semester':
        dates.start = today.subtract('6', 'month').startOf('day').toISOString()
        break
      default:
        dates.start = today.startOf('day').toISOString()
        break
    }
    setDates(dates)
  }

  const getFilterValue = () => {
    if (dates.label === 'personalized') {
      return ''
    }
    return dates.label
  }

  const headCounters = [
    {
      label: 'Pendências à resolver',
      icon: <NewPendencyIcon />,
      total: counters?.data.pendentPendencies,
      // textColor: 'danger500' as keyof typeof theme.ds.colors,
      highlightColor: 'terciary300' as keyof typeof theme.ds.colors,
      avg: '',
      tooltipMessage:
        'Quando o médico/secretária ou paciente respondeu sua pendência, e você precisa resolvê-la.',
      status: SurgicalPendencyStatus.ANSWERED,
      state: {
        pendency: true,
        surgical_pendency_status: SurgicalPendencyStatus.ANSWERED
      }
    },
    {
      label: 'Pendências em andamento',
      icon: <NewPendencyIcon />,
      total: counters?.data.inProgressPendencies,
      // textColor: 'danger500' as keyof typeof theme.ds.colors,
      highlightColor: 'secondary300' as keyof typeof theme.ds.colors,
      avg: '',
      status: SurgicalPendencyStatus.PENDENT,
      tooltipMessage:
        'Quando você criou uma pendência, e ela está em andamento aguardando o retorno do médico/secretária ou paciente.',
      state: {
        pendency: true,
        surgical_pendency_status: SurgicalPendencyStatus.PENDENT
      }
    },
    {
      label: 'Pendências resolvidas',
      icon: <NewPendencyIcon />,
      total: counters?.data.resolvedPendencies,
      avg: '',
      highlightColor: 'success500' as keyof typeof theme.ds.colors,
      tooltipMessage: 'Pendências que foram resolvidas e finalizadas.',
      status: SurgicalPendencyStatus.RESOLVED,
      state: {
        pendency: true,
        surgical_pendency_status: SurgicalPendencyStatus.RESOLVED
      }
    },
    {
      label: 'Solicitações de cancelamento',
      icon: <CancelationRequestIcon />,
      total: counters?.data.requestedCancellation,
      avg: '',
      highlightColor: 'terciary300' as keyof typeof theme.ds.colors,
      tooltipMessage: 'Pedidos cirúrgicos com solicitação de cancelamento.',
      status: SurgicalOrderStatus.CANCELLATION_REQUEST
    },
    {
      label: 'Conversas à resolver',
      icon: <ChatReversoIcon />,
      total: counters?.data.conversationsToResolve,
      avg: '',
      highlightColor: 'terciary300' as keyof typeof theme.ds.colors,
      tooltipMessage: 'Conversas que precisam de resolução ou resposta.',
      status: SurgicalPendencyStatus.ANSWERED,
      state: {
        pendency: true,
        surgical_pendency_status: SurgicalPendencyStatus.ANSWERED,
        surgical_pendency_type: 'DOCTOR_COMMUNICATION'
      }
    },
    {
      label: 'Conversas em andamento',
      icon: <ChatReversoIcon />,
      total: counters?.data.conversationsInProgress,
      avg: '',
      highlightColor: 'secondary300' as keyof typeof theme.ds.colors,
      tooltipMessage: 'Conversas que estão em andamento aguardando retorno.',
      status: SurgicalPendencyStatus.PENDENT,
      state: {
        pendency: true,
        surgical_pendency_status: SurgicalPendencyStatus.PENDENT,
        surgical_pendency_type: 'DOCTOR_COMMUNICATION'
      }
    },
    {
      label: 'Conversas resolvidas',
      icon: <ChatReversoIcon />,
      total: counters?.data.conversationsResolved,
      avg: '',
      highlightColor: 'success500' as keyof typeof theme.ds.colors,
      tooltipMessage: 'Conversas que foram resolvidas e finalizadas.',
      status: SurgicalPendencyStatus.RESOLVED,
      state: {
        pendency: true,
        surgical_pendency_status: SurgicalPendencyStatus.RESOLVED,
        surgical_pendency_type: 'DOCTOR_COMMUNICATION'
      }
    },
    {
      label: 'Autorização comunicada por paciente',
      icon: <MedicalPrescritionIcon />,
      total: counters?.data.authorizedCommunication,
      // textColor: 'danger500' as keyof typeof theme.ds.colors,
      highlightColor: 'info400' as keyof typeof theme.ds.colors,
      avg: '',
      status: '',
      // tooltipMessage: '',
      with_authorized_communication: true
    }
  ]

  const AuthorizationProcessCounters = [
    {
      label: 'Pedidos solicitados por pacientes',
      total: counters?.data.orderedByPatient,
      avg: '',
      highlightColor: 'secondary500' as keyof typeof theme.ds.colors,
      // tooltipMessage: '',
      status: SurgicalOrderStatus.ORDER_REGISTERED
    },
    {
      label: 'Revisar',
      total: counters?.data.review,
      avg: '',
      highlightColor: 'secondary500' as keyof typeof theme.ds.colors,
      // tooltipMessage: '',
      status: SurgicalOrderStatus.REVIEW
    },
    {
      label: 'Pré-agendamento',
      total: counters?.data.preSchedule,
      avg: '',
      highlightColor: 'secondary500' as keyof typeof theme.ds.colors,
      // tooltipMessage: '',
      status: SurgicalOrderStatus.PRE_SCHEDULE
    },
    {
      label: 'Cotação de OPME',
      total: counters?.data.opmeQuotation,
      avg: '',
      highlightColor: 'secondary500' as keyof typeof theme.ds.colors,
      // tooltipMessage: '',
      status: SurgicalOrderStatus.OPME_QUOTATION
    },
    {
      label: 'Aguardando retorno do convênio',
      total: counters?.data.inHealthInsurance,
      avg: '',
      highlightColor: 'secondary500' as keyof typeof theme.ds.colors,
      // tooltipMessage: '',
      status: SurgicalOrderStatus.IN_HEALTH_INSURANCE
    },
    {
      label: 'Validação CRMO',
      total: counters?.data.crmoValidation,
      avg: '',
      highlightColor: 'secondary500' as keyof typeof theme.ds.colors,
      // tooltipMessage: '',
      status: SurgicalOrderStatus.CRMO_VALIDATION
    },
    {
      label: 'Na CRMO',
      total: counters?.data.crmo,
      avg: '',
      highlightColor: 'secondary500' as keyof typeof theme.ds.colors,
      // tooltipMessage: '',
      status: SurgicalOrderStatus.CRMO
    },
    {
      label: 'Pacote cirúrgico',
      total: counters?.data.isPackage,
      avg: '',
      highlightColor: 'secondary500' as keyof typeof theme.ds.colors,
      // tooltipMessage: '',
      // status: SurgicalOrderStatus.SURGICAL_PACKAGE // TODO: Review
      is_package: true
    }
  ]

  const FinalizedCounters = [
    {
      label: 'Autorizados',
      total: counters?.data.authorized,
      avg: '',
      highlightColor: 'primary300' as keyof typeof theme.ds.colors,
      // tooltipMessage: '',
      status: AuthorizationStatus.AUTHORIZED
    },
    {
      label: 'Autorizados com divergência',
      total: counters?.data.partiallyAuthorized,
      avg: '',
      highlightColor: 'primary300' as keyof typeof theme.ds.colors,
      // tooltipMessage: '',
      status: AuthorizationStatus.PARTIALLY_AUTHORIZED
    },
    {
      label: 'Reanálise',
      total: counters?.data?.reanalysis,
      avg: '',
      highlightColor: 'secondary400' as keyof typeof theme.ds.colors,
      // tooltipMessage: '',
      status: AuthorizationStatus.REANALYSIS
    },
    {
      label: 'Negados',
      total: counters?.data.denied,
      avg: '',
      highlightColor: 'terciary500' as keyof typeof theme.ds.colors,
      // tooltipMessage: '',
      status: AuthorizationStatus.DENIED
    },
    {
      label: 'Realizados',
      total: Sum(
        parseInt(counters?.data.realized ?? '0'),
        parseInt(counters?.data.serviceEvaluation ?? '0')
      ),
      avg: '',
      highlightColor: 'primary300' as keyof typeof theme.ds.colors,
      // tooltipMessage: '',
      status: SurgicalOrderStatus.CONCLUDED
    },
    {
      label: 'Cancelados',
      total: counters?.data.cancelled,
      avg: '',
      highlightColor: 'neutral400' as keyof typeof theme.ds.colors,
      // tooltipMessage: '',
      status: SurgicalOrderStatus.CANCELLED
    },
    {
      label: 'Senhas vencidas',
      total: counters?.data.expiredPasswords,
      avg: '',
      highlightColor: 'danger700' as keyof typeof theme.ds.colors,
      // tooltipMessage: '',
      status: '',
      expired_passwords: true
    }
  ]

  const SchedulingStatusCounters = [
    {
      label: 'Pedidos agendados',
      icon: <CalendarCheckIcon />,
      avg: '',
      total: counters?.data.scheduled?.total,
      status: SurgicalOrderStatus.SCHEDULED,
      onClick: () =>
        history.push(`/pedidos/${getSlugByText('Pedidos agendados')}`, {
          status: SurgicalOrderStatus.SCHEDULED,
          start_date: dates.start,
          end_date: dates.end,
          label: dates.label
        }),
      subCounters: [
        {
          subCounterText: 'Autorizados',
          subCounterValue: counters?.data.scheduled?.approved,
          subCounterValueColor: 'primary300' as keyof typeof theme.ds.colors,
          onClickSubCounter: () =>
            history.push(
              `/pedidos/${getSlugByText('Pedidos agendados autorizados')}`,
              {
                status: SurgicalOrderStatus.SCHEDULED_AUTHORIZED,
                start_date: dates.start,
                end_date: dates.end,
                label: dates.label
              }
            )
        },
        {
          subCounterText: 'Em processo de autorização',
          subCounterValue: counters?.data.scheduled?.disapproved,
          subCounterValueColor: 'secondary400' as keyof typeof theme.ds.colors,
          onClickSubCounter: () =>
            history.push(
              `/pedidos/${getSlugByText(
                'Pedidos agendados em processo de autorização'
              )}`,
              {
                status: SurgicalOrderStatus.SCHEDULED_NOT_AUTHORIZED,
                start_date: dates.start,
                end_date: dates.end,
                label: dates.label
              }
            )
        }
      ]
    },
    {
      label: 'Pedidos não agendados',
      icon: <CalendarCancelIcon />,
      avg: '',
      total: counters?.data.unscheduled?.total,
      status: SurgicalOrderStatus.UNSCHEDULED,
      onClick: () =>
        history.push(`/pedidos/${getSlugByText('Pedidos não agendados')}`, {
          status: SurgicalOrderStatus.UNSCHEDULED,
          start_date: dates.start,
          end_date: dates.end,
          label: dates.label
        }),
      subCounters: [
        {
          subCounterText: 'Autorizados',
          subCounterValue: counters?.data.unscheduled?.approved,
          subCounterValueColor: 'primary300' as keyof typeof theme.ds.colors,
          onClickSubCounter: () =>
            history.push(
              `/pedidos/${getSlugByText('Pedidos não agendados autorizados')}`,
              {
                status: SurgicalOrderStatus.UNSCHEDULED_AUTHORIZED,
                start_date: dates.start,
                end_date: dates.end,
                label: dates.label
              }
            )
        },
        {
          subCounterText: 'Em processo de autorização',
          subCounterValue: counters?.data.unscheduled?.disapproved,
          subCounterValueColor: 'secondary400' as keyof typeof theme.ds.colors,
          onClickSubCounter: () =>
            history.push(
              `/pedidos/${getSlugByText(
                'Pedidos não agendados em processo de autorização'
              )}`,
              {
                status: SurgicalOrderStatus.UNSCHEDULED_NOT_AUTHORIZED,
                start_date: dates.start,
                end_date: dates.end,
                label: dates.label
              }
            )
        }
      ]
    }
  ]

  useEffect(() => {
    if (isLoaded) {
      if (moment(dates.start).isSameOrBefore(moment(dates.end))) {
        changeFilter &&
          changeFilter(
            dates.start ?? moment().utc().startOf('day').toISOString(),
            dates.end ?? moment().utc().endOf('day').toISOString(),
            dates.label
          )
      }
    } else {
      setIsLoaded(true)
    }
  }, [dates])

  useEffect(() => {
    isLoaded &&
      changeFilter &&
      changeFilter(
        dates.start ?? moment().utc().startOf('day').toISOString(),
        dates.end ?? moment().utc().endOf('day').toISOString()
      )
  }, [hospitalName])

  return (
    <ContainerNew>
      <S.Header>
        <HeadingNew size="xxlarge" color="secondary700">
          Pedidos {hospitalName}
        </HeadingNew>
        <S.InputContainer>
          <SelectFieldNew
            label="Período"
            labelOverlapping
            labelColor="primary600"
            labelSize="xxsmall"
            labelWeight="semiBold"
            initialValue="today"
            items={filterValues}
            onInputChange={changeInterval}
            placeholder={'Personalizado'}
            value={getFilterValue()}
          />
          <S.InputCalendar>
            <TextFieldNew
              label="Data início"
              labelColor="primary600"
              labelSize="xxsmall"
              labelWeight="semiBold"
              labelOverlapping
              mask={dateMask}
              icon={<CalendarRange />}
              iconPosition="insideRight"
              iconIsButton
              iconColor="neutral700"
              iconBgColor="transparent"
              onIconClick={() => toggleCalendar('start')}
              value={dateInputFormat(moment(dates.start ?? '').toISOString())}
            />
            <S.StyledCalendar
              show={showCalendar === 'start'}
              onDateChange={(date: any) => changeDate(date as Date)}
              minDate={moment().subtract(1, 'year').endOf('day').toDate()}
              maxDate={moment().endOf('day').toDate()}
            />
          </S.InputCalendar>
          <S.InputCalendar>
            <TextFieldNew
              label="Data fim"
              labelColor="primary600"
              labelSize="xxsmall"
              labelWeight="semiBold"
              labelOverlapping
              mask={dateMask}
              icon={<CalendarRange />}
              iconPosition="insideRight"
              iconIsButton
              iconColor="neutral700"
              iconBgColor="transparent"
              onIconClick={() => toggleCalendar('end')}
              value={dateInputFormat(
                moment(dates.end ?? '')
                  .subtract(4, 'hours')
                  .toISOString()
              )}
            />
            <S.StyledCalendar
              show={showCalendar === 'end'}
              onDateChange={(date: any) => changeDate(date as Date)}
              minDate={moment().subtract(1, 'year').endOf('day').toDate()}
              maxDate={moment().endOf('day').toDate()}
            />
          </S.InputCalendar>
          <CardCounter
            style={{ height: '48px' }}
            text={
              <HeadingNew
                as="h2"
                size="xsmall"
                color="primary600"
                weight="bold"
                style={{ marginLeft: 0, marginRight: '16px', width: '100%' }}
              >
                Todos os pedidos
              </HeadingNew>
            }
            countValue={counters?.data.total}
            color="primary600"
            countValueColor="primary600"
            bgColor="primary50"
            padding="16px 24px"
            onClick={() =>
              history.push(`/pedidos/${getSlugByText('Todos os pedidos')}`, {
                status: undefined,
                start_date: dates.start,
                end_date: dates.end,
                label: dates.label
              })
            }
          />

          {user.groups?.includes(Profile.CRMO) && (
            <ButtonNew
              size="large"
              onClick={() => history.push('/pedido/novo')}
              data-testid="new-surgical-order-button"
              style={{ fontSize: '14px' }}
            >
              Novo pedido cirúrgico
            </ButtonNew>
          )}
        </S.InputContainer>
      </S.Header>

      <S.HeadCountersWrapper>
        {headCounters.map((count, index) => (
          <TooltipNew
            key={index}
            text={count.tooltipMessage ?? ''}
            cursor="pointer"
            placement="top"
          >
            <CardCounter
              style={{ flex: 1 }}
              text={count.label}
              countValue={count.total}
              icon={count.icon}
              iconColor={count.highlightColor}
              countValueColor={count.highlightColor}
              onClick={() =>
                history.push(`/pedidos/${getSlugByText(count.label)}`, {
                  state: count.state,
                  status: count.status,
                  start_date: dates.start,
                  end_date: dates.end,
                  expired_passwords: (count as any)?.expired_passwords,
                  with_authorized_communication: (count as any)
                    ?.with_authorized_communication,
                  conversations_to_resolve: (count as any)
                    ?.conversations_to_resolve,
                  conversations_in_progress: (count as any)
                    ?.conversations_in_progress,
                  conversations_resolved: (count as any)?.conversations_resolved
                })
              }
            />
          </TooltipNew>
        ))}
      </S.HeadCountersWrapper>

      <S.CountersWrapper>
        <S.StyledAccordion key="accordion1" elevation={0} defaultExpanded>
          <S.StyledAccordionSummary
            expandIcon={<AccordionArrow />}
            aria-controls="accordion1-content"
            id="accordion1-header"
          >
            <HeadingNew as="h4" size="large">
              Pedidos em processo de autorização
            </HeadingNew>
          </S.StyledAccordionSummary>
          <S.StyledAccordionDetails>
            <S.AuthorizationProcessCountersWrapper>
              {AuthorizationProcessCounters.map((count, index) => (
                <CardCounter
                  key={index}
                  style={{ flex: 1 }}
                  text={count.label}
                  countValue={count.total}
                  countValueColor={count.highlightColor}
                  onClick={() =>
                    history.push(`/pedidos/${getSlugByText(count.label)}`, {
                      status: count.status,
                      start_date: dates.start,
                      end_date: dates.end,
                      label: dates.label,
                      expired_passwords: (count as any)?.expired_passwords,
                      is_package: count?.is_package
                    })
                  }
                />
              ))}
            </S.AuthorizationProcessCountersWrapper>
          </S.StyledAccordionDetails>
        </S.StyledAccordion>
      </S.CountersWrapper>

      <S.CountersWrapper>
        <S.StyledAccordion key="accordion2" elevation={0} defaultExpanded>
          <S.StyledAccordionSummary
            expandIcon={<AccordionArrow />}
            aria-controls="accordion2-content"
            id="accordion2-header"
          >
            <HeadingNew as="h4" size="large">
              Pedidos finalizados
            </HeadingNew>
          </S.StyledAccordionSummary>
          <S.StyledAccordionDetails>
            <S.FinalizedCountersWrapper>
              {FinalizedCounters.map((count, index) => (
                <CardCounter
                  key={index}
                  style={{
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    textAlign: 'center',
                    flex: 1
                  }}
                  text={count.label}
                  countValue={count.total}
                  countValueColor={count.highlightColor}
                  onClick={() =>
                    history.push(`/pedidos/${getSlugByText(count.label)}`, {
                      status: count.status,
                      start_date: dates.start,
                      end_date: dates.end,
                      label: dates.label,
                      expired_passwords: (count as any)?.expired_passwords
                    })
                  }
                />
              ))}
            </S.FinalizedCountersWrapper>
          </S.StyledAccordionDetails>
        </S.StyledAccordion>
      </S.CountersWrapper>

      <S.CountersWrapper>
        <S.StyledAccordion key="accordion3" elevation={0} defaultExpanded>
          <S.StyledAccordionSummary
            expandIcon={<AccordionArrow />}
            aria-controls="accordion3-content"
            id="accordion3-header"
          >
            <HeadingNew as="h4" size="large">
              Status de agendamento
            </HeadingNew>
          </S.StyledAccordionSummary>
          <S.StyledAccordionDetails>
            <S.SchedulingCountersWrapper>
              {SchedulingStatusCounters.map((counter, index) => (
                <CardCounterMultiple
                  key={index}
                  text={counter.label}
                  countValue={counter.total}
                  icon={counter.icon}
                  onClick={counter.onClick}
                  subCounters={counter.subCounters}
                />
              ))}
            </S.SchedulingCountersWrapper>
          </S.StyledAccordionDetails>
        </S.StyledAccordion>
      </S.CountersWrapper>
    </ContainerNew>
  )
}

const filterValues = [
  {
    label: 'Hoje',
    value: 'today'
  },
  {
    label: 'Ontem',
    value: 'yesterday'
  },
  {
    label: 'Semana',
    value: 'week'
  },
  {
    label: 'Mês',
    value: 'month'
  },
  {
    label: 'Trimestre',
    value: 'trimester'
  },
  {
    label: 'Semestre',
    value: 'semester'
  }
]
